import React, { useState } from "react";

import Button from "../Button";
import Icon from "../Icon";
import TextField from "../TextField";
import checkTicketAccess from "../../helpers/checkTicketAccess";
import createProfile from "../../helpers/createProfile";
import createUserSilently from "../../helpers/createUserSilently";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import lookupTicket from "../../helpers/lookupTicket";
import signInUser from "../../helpers/signInUser";
import { useForm, Controller } from "react-hook-form";

type Inputs = {
  email: string;
  password: string;
};

const SignInForm = () => {
  const { register, handleSubmit, errors, control, formState } =
    useForm<Inputs>({
      mode: "onChange",
    });
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Verify user
  const verify = async (email: string, password: string) => {
    try {
      const code = password.toString().toLowerCase();

      // Look up tickets
      const ticket = await lookupTicket(email, code);
      if (!ticket) {
        throw Error("Incorrect email or ticket code.");
      }

      // Check ticket access
      const { access } = ticket;
      if (!checkTicketAccess(access)) {
        throw Error("Your event is not yet available. Please try again later.");
      }

      // Sign up user silently
      await createUserSilently(email, code.toUpperCase());

      // Sign in using email and ticket code
      const user = await signInUser(email, code.toUpperCase());
      if (!user) {
        throw Error("Unable to sign in.");
      }

      // Create a profile
      await createProfile(user.uid, {
        firstname: ticket.firstname,
        lastname: ticket.lastname,
        email,
      });

      return ticket;
    } catch (error) {
      setIsSubmitting(false);

      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred.");
      }

      console.error(error);
    }
  };

  const onSubmit = (data: Inputs) => {
    setIsSubmitting(true);
    setError("");
    verify(data.email, data.password);
  };

  return (
    <div className="w-full">
      <form
        className="flex flex-wrap justify-center space-y-4 lg:space-x-4 lg:flex-no-wrap lg:space-y-0"
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextField
          type="email"
          name="email"
          placeholder="Email Address"
          register={register({
            required: "Required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z0-9]{2,}$/i,
              message: "Invalid email address.",
            },
          })}
          error={errors.email?.message}
        />
        <Controller
          control={control}
          name="password"
          defaultValue=""
          rules={{
            required: "Required",
            minLength: {
              value: 6,
              message: "Ticket code should have a minimum of 6 characters.",
            },
            setValueAs: (value) => value.toString().toUpperCase(),
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <TextField
              type="text"
              name={name}
              placeholder="Ticket Code"
              value={value.toString().toUpperCase()}
              onChange={onChange}
              onBlur={onBlur}
              register={ref}
              error={errors.password?.message}
            />
          )}
        />
        <Button
          type="submit"
          disabled={!formState.isValid}
          isLoading={isSubmitting}
        >
          Join
        </Button>
      </form>
      {error ? (
        <div className="w-full mt-4 text-red-600 rounded">
          <Icon icon={faTimes} /> {error}
        </div>
      ) : null}
    </div>
  );
};

export default SignInForm;
